import React, { useState, useEffect, useRef } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "./index.scss";
import _ from "lodash";
import Moment from "moment";

const DateRangePicker = ({ config = {} }) => {
  const initialStart = Moment(
    _.get(config, "startAt", Moment().startOf("day").valueOf())
  ).startOf("day");
  const initialEnd = Moment(
    _.get(config, "endAt", Moment().endOf("day").valueOf())
  ).endOf("day");

  const onChange = _.get(config, "onChange", () => {});
  const dateFormat = _.get(config, "format", "MM/DD/YYYY");
  const timeFormat = _.get(config, "timeFormat", "HH:mm");
  const showTimeSelect = _.get(config, "showTimeSelect", false);
  const className = _.get(config, "className", "");

  const [startDate, setStartDate] = useState(Moment(initialStart));
  const [endDate, setEndDate] = useState(Moment(initialEnd));
  const [isOpen, setIsOpen] = useState(false);
  const pickerRef = useRef(null);

  const formatDate = (timestamp) => {
    if (!timestamp) return "";
    return Moment(timestamp).format(
      showTimeSelect ? `${dateFormat} ${timeFormat}` : dateFormat
    );
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (pickerRef.current && !pickerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  const handleStartChange = (date) => {
    const newStart = Moment(date).startOf("day");
    setStartDate(newStart);

    if (newStart.isAfter(endDate)) {
      setEndDate(newStart.clone().endOf("day"));
    }
  };

  const handleEndChange = (date) => {
    setEndDate(Moment(date).endOf("day"));
  };

  const handleApply = () => {
    onChange({
      start: startDate.valueOf(),
      end: endDate.valueOf(),
    });
    setIsOpen(false);
  };

  const handleCancel = () => {
    setStartDate(initialStart.clone().startOf("day"));
    setEndDate(initialEnd.clone().endOf("day"));
    setIsOpen(false);
  };

  return (
    <div className={`date-range-picker ${className}`} ref={pickerRef}>
      <div
        className="date-input"
        onClick={() => setIsOpen(!isOpen)}
        aria-haspopup="true"
        aria-expanded={isOpen}
      >
        <span>
          {formatDate(startDate.valueOf()) || "Start date"} -{" "}
          {formatDate(endDate.valueOf()) || "End date"}
        </span>
        <i className="fa fa-calendar-alt"></i>
      </div>

      {isOpen && (
        <div className="datepicker-popup">
          <div className="body">
            <div className="datepicker-column">
              <DatePicker
                selected={startDate.toDate()}
                onChange={handleStartChange}
                selectsStart
                startDate={startDate.toDate()}
                endDate={endDate.toDate()}
                maxDate={endDate.toDate()}
                showTimeSelect={showTimeSelect}
                timeFormat={timeFormat}
                timeIntervals={15}
                dateFormat={
                  showTimeSelect ? `${dateFormat} ${timeFormat}` : dateFormat
                }
                inline
              />
            </div>

            <div className="datepicker-column">
              <DatePicker
                selected={endDate.toDate()}
                onChange={handleEndChange}
                selectsEnd
                startDate={startDate.toDate()}
                endDate={endDate.toDate()}
                minDate={startDate.toDate()}
                showTimeSelect={showTimeSelect}
                timeFormat={timeFormat}
                timeIntervals={15}
                dateFormat={
                  showTimeSelect ? `${dateFormat} ${timeFormat}` : dateFormat
                }
                inline
              />
            </div>
          </div>

          <div className="footer">
            <div className="selected-range">
              {formatDate(startDate.valueOf())} -{" "}
              {formatDate(endDate.valueOf())}
            </div>
            <div className="actions">
              <button
                className="btn cancel"
                onClick={handleCancel}
                aria-label="Cancel selection"
              >
                Cancel
              </button>
              <button
                className="btn apply"
                onClick={handleApply}
                aria-label="Apply selection"
              >
                Apply
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DateRangePicker;
